/**
 * الخريطة.
 * ديسكتوب: خريطة flex:3 + لوحة flex:1 على اليسار. أقل من ~740px اللوحة تنزل أسفلها.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { color, mapRamp } from '@/design/tokens';
import { Card, SegmentedControl } from '@/components/primitives/Primitives';
import { ErrorState, LoadingState } from '@/components/states/States';
import { MapSurface, type MapSurfaceMessage } from '@/components/map/MapSurface';
import { useMapMetrics } from '@/services/market';
import { numOrDash } from '@/lib/format';
import type { CityName, MapMode } from '@/domain/types';

const MODES: { value: MapMode; label: string }[] = [
  { value: 'price', label: 'السعر' },
  { value: 'opportunity', label: 'الفرص' },
  { value: 'momentum', label: 'النمو' },
  { value: 'liquidity', label: 'السيولة' },
  { value: 'pressure', label: 'ضغط السوق' },
];

const CITIES: { value: CityName; label: string }[] = [
  { value: 'الرياض', label: 'الرياض' },
  { value: 'جدة', label: 'جدة' },
];

export default function MapRoute(): JSX.Element {
  const navigate = useNavigate();
  const [mode, setMode] = useState<MapMode>('opportunity');
  const [city, setCity] = useState<CityName>('الرياض');
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const metrics = useMapMetrics(city, mode);

  const onMessage = useCallback(
    (m: MapSurfaceMessage) => {
      if (m.type === 'select-district' && m.name) setSelected(m.name);
      if (m.type === 'open-district' && m.name && m.city) {
        navigate(`/district/${encodeURIComponent(`${m.city}|${m.name}`)}`);
      }
      if (m.type === 'mode-changed' && m.mode) setMode(m.mode);
    },
    [navigate],
  );

  const ranked = useMemo(() => {
    if (!metrics.data) return [];
    return [...metrics.data.cells]
      .filter((c) => c.value !== null)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
      .slice(0, 10);
  }, [metrics.data]);

  const ramp = mapRamp[mode];

  return (
    <div className="nq-enter" style={{ paddingTop: 36 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <SegmentedControl options={MODES} value={mode} onChange={setMode} ariaLabel="نمط الخريطة" />
        <span style={{ flex: 1 }} />
        <SegmentedControl options={CITIES} value={city} onChange={setCity} ariaLabel="المدينة" />
      </div>

      <div className="nq-row" style={{ alignItems: 'stretch' }}>
        <div style={{ flex: '3 1 420px', minWidth: 0 }}>
          <MapSurface city={city} mode={mode} focusDistrictName={selected} height={440} onMessage={onMessage} />
        </div>

        <div style={{ flex: '1 1 280px', minWidth: 0, display: 'grid', gap: 14 }}>
          <Card variant="secondary">
            <div className="nq-label">مفتاح النمط — {metrics.data?.modeLabel ?? MODES.find((m) => m.value === mode)?.label}</div>
            <div
              aria-hidden="true"
              style={{
                marginTop: 10,
                height: 10,
                borderRadius: 6,
                background: `linear-gradient(to left, ${ramp.join(',')})`,
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11.5, color: color.label }}>
              <span className="nq-num" dir="ltr">{numOrDash(metrics.data?.legendLow)}</span>
              <span className="nq-num" dir="ltr">{numOrDash(metrics.data?.legendHigh)}</span>
            </div>
            <p className="nq-micro" style={{ marginTop: 8 }}>
              بيانات تجريبية · الوحدة: {metrics.data?.unit ?? '—'}
            </p>
          </Card>

          <Card variant="secondary">
            <div className="nq-label">أعلى ١٠ أحياء في هذا النمط</div>
            {metrics.status === 'loading' ? (
              <div style={{ marginTop: 12 }}>
                <LoadingState message="نحمّل طبقة الخريطة..." blocks={2} />
              </div>
            ) : metrics.status === 'error' ? (
              <div style={{ marginTop: 12 }}>
                <ErrorState message="تعذّر تحميل طبقة الخريطة." onRetry={metrics.reload} />
              </div>
            ) : (
              <ol style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
                {ranked.map((c, i) => (
                  <li key={c.districtId}>
                    <button
                      type="button"
                      onClick={() => setSelected(c.name)}
                      onDoubleClick={() => navigate(`/district/${encodeURIComponent(c.districtId)}`)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        padding: '9px 10px',
                        background: selected === c.name ? 'rgba(0,229,199,.08)' : 'transparent',
                        border: 0,
                        borderRadius: 9,
                        cursor: 'pointer',
                        color: color.textBody,
                        fontSize: 13,
                        minHeight: 44,
                      }}
                    >
                      <span>
                        <span className="nq-num" dir="ltr" style={{ color: color.label, marginInlineEnd: 8 }}>
                          {i + 1}
                        </span>
                        {c.name}
                      </span>
                      <span className="nq-num" dir="ltr" style={{ color: color.text, fontWeight: 600 }}>
                        {numOrDash(c.value)}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          {selected ? (
            <Card variant="secondary">
              <div className="nq-label">الحي المختار</div>
              <div style={{ fontSize: 17, fontWeight: 600, marginTop: 6 }}>{selected}</div>
              <button
                type="button"
                className="nq-btn nq-btn--primary nq-btn--block"
                style={{ marginTop: 14 }}
                onClick={() => navigate(`/district/${encodeURIComponent(`${city}|${selected}`)}`)}
              >
                افتح الحي
              </button>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
